import { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { onSendProfileConfig } from "../../../profile/presentation/store/OnboardingThunk";
import { setResetSearch } from "../../../match/presentation/store/MatchSlice";

interface UseDebouncedConfigProps<T> {
	initialValues: T;
	debounceTime?: number;
	buildUpdatedFilters: (settings: T) => Record<string, any>;
	customConditions?: (debounceValue: T, initialValues: T) => boolean;
}

export const useDebouncedConfig = <T>({ initialValues, debounceTime = 2000, buildUpdatedFilters, customConditions }: UseDebouncedConfigProps<T>): [T, React.Dispatch<React.SetStateAction<T>>] => {
	const dispatch: AppDispatch = useDispatch();
	const store = useStore<RootState>();
	const accountId = useRef(store.getState().onboarding.user?.id);
	const [settings, setSettings] = useState(initialValues);
	const incoming = useRef(initialValues);
	incoming.current = initialValues;
	const initialKey = JSON.stringify(initialValues);
	const current = useRef({ value: initialValues, baseline: initialValues, edited: false, dirty: false, saving: false, flushAfterSave: false });
	const timer = useRef<ReturnType<typeof setTimeout>>();
	const flush = useRef(() => {});
	flush.current = () => {
		const pending = current.current;
		if (!pending.dirty) return;
		if (accountId.current !== store.getState().onboarding.user?.id) return;
		if (pending.saving) {
			pending.flushAfterSave = true;
			return;
		}
		const unchanged = customConditions
			? customConditions(pending.value, pending.baseline)
			: JSON.stringify(pending.value) === JSON.stringify(pending.baseline);
		pending.dirty = false;
		if (unchanged) return;
		const previous = pending.baseline;
		pending.baseline = pending.value;
		pending.saving = true;
		const request = dispatch(onSendProfileConfig({ data: buildUpdatedFilters(pending.value) }));
		Promise.resolve(request).then((result) => {
			if (result.meta.requestStatus === "rejected") pending.baseline = previous;
			else dispatch(setResetSearch());
		}).finally(() => {
			pending.saving = false;
			if (pending.flushAfterSave) {
				pending.flushAfterSave = false;
				flush.current();
			}
		});
	};

	// Server values may arrive after mounting; never overwrite a local edit.
	useEffect(() => {
		if (current.current.edited) return;
		current.current.value = incoming.current;
		current.current.baseline = incoming.current;
		setSettings(incoming.current);
	}, [initialKey]);

	const updateSettings = useCallback<React.Dispatch<React.SetStateAction<T>>>((update) => {
		const next = typeof update === "function" ? (update as (value: T) => T)(current.current.value) : update;
		current.current.value = next;
		current.current.edited = true;
		current.current.dirty = true;
		setSettings(next);
		clearTimeout(timer.current);
		timer.current = setTimeout(() => flush.current(), debounceTime);
	}, [debounceTime]);

	useEffect(() => () => {
		clearTimeout(timer.current);
		flush.current();
	}, []);

	return [settings, updateSettings];
};
